import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import {Button} from "@mui/material";
import {ReactComponent as BannerIcon} from "assets/img/banner_icon.svg";
import {setBannerViewState} from "modules/actions/user";
import {getIsBannerViewed, getSettings} from "modules/selectors";
import {BannerHelper} from "modules/utils/banner_helper";
import {closeApplication} from "modules/utils/SSO";
import React, {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useBeforeUnload} from "react-router-dom";
import {some} from "lodash";
import RallySSO from "modules/utils/SSO/rallySSO";

const Wrapper = styled.div`
	display: flex;
	justify-content: flex-start;
	gap: 8px;
	align-items: center;
	border-radius: 8px;
	background: #ffffff;
	box-shadow: 0 4px 9px 0 rgba(0, 0, 0, 0.24);
	position: fixed;
	bottom: 12px;
	right: 16px;
	left: 16px;
	z-index: 14;
	padding: 10px 8px;

	@media screen and (min-width: 880px) {
		max-width: 360px;
		width: 100%;
		left: unset;
	}
`;

const InfoBlock = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	gap: 4px;

	h4 {
		color: #070a0d;
		font-size: 16px;
		font-style: normal;
		font-weight: 700;
		line-height: 100%;
		letter-spacing: 0.32px;
	}

	p {
		color: #070a0d;
		font-size: 14px;
		font-style: normal;
		font-weight: 400;
		line-height: 130%; /* 21px */
		text-align: left;
	}
`;

const CloseButton = styled(Button)`
	width: 24px;
	height: 24px;
	min-width: 24px;
	padding: 24px;
	color: #57606c;
	margin-left: auto;

	svg {
		height: 32px;
		width: auto;
	}
`;

const ClickArea = styled(Button)`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	gap: 8px;
	height: unset;
	min-height: 40px;

	svg {
		height: 54px;
		width: 54px;
	}
`;

export const NotificationBanner: React.FC = () => {
	const dispatch = useDispatch();
	const isViewedFromUser = useSelector(getIsBannerViewed);
	const isForceView = Boolean(useSelector(getSettings)?.banner.isForceHub);
	const isVisibleFromStore = BannerHelper.getIsNotificationVisibleFromStorage();
	const [isOpen, setIsOpen] = useState(!isViewedFromUser);
	const i18n = useTranslation();
	const hasBanner = some([isVisibleFromStore && !isViewedFromUser, isForceView]);
	const isNative = RallySSO.IS_NATIVE;

	// useEffect(() => {
	// 	if (isForceView) {
	// 		setIsOpen(true);
	// 	}
	// }, [isForceView]);

	useEffect(() => {
		if (hasBanner) {
			setIsOpen(true);
		}
	}, [hasBanner]);

	useBeforeUnload(() => {
		BannerHelper.clearNotification(isOpen);
	});

	const close = useCallback(() => {
		if (isNative) {
			setIsOpen(false);
			dispatch(setBannerViewState({bannerViewsCount: 1}));
		} else {
			const counter = BannerHelper.getCloseBannerCounter();
			if (counter >= 2 && !isForceView) {
				dispatch(setBannerViewState({bannerViewsCount: 3}));
			}
			BannerHelper.closeNotification();
			setIsOpen(false);
		}
	}, [isForceView]);

	if (!isOpen) {
		return null;
	}

	return (
		<Wrapper>
			<ClickArea onClick={closeApplication}>
				<BannerIcon />
				<InfoBlock>
					<h4>{i18n.t("banner_helper.copy.title", "MLB Play")}</h4>
					<p>{i18n.t("banner_helper.copy.text", "Test your baseball knowledge")}</p>
				</InfoBlock>
			</ClickArea>

			<CloseButton onClick={close} aria-label="Close notification banner">
				<CloseIcon />
			</CloseButton>
		</Wrapper>
	);
};
